import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { Media } from '@/js/libs/ui';
import { TableLegacy, Checkbox, TableCellContentTypeLegacy } from '@/js/libs/ui/src/V2';

import type { SortProps } from 'src/hooks/useSortAndOrderState';
import type { ColumnComponentPropsV2Legacy, ColumnV2Legacy } from 'src/types';

export interface TableHeaderLegacyProps<
  TColumnName extends string,
  TColumnProps extends ColumnComponentPropsV2Legacy<TColumnName> = ColumnComponentPropsV2Legacy<TColumnName>,
> {
  getSortProps?: SortProps<TColumnName>;
  columns: ColumnV2Legacy<TColumnName, TColumnProps>[];
  isSticky?: boolean;
}

const TableHeaderLegacy = <
  TColumnName extends string,
  TColumnProps extends ColumnComponentPropsV2Legacy<TColumnName> = ColumnComponentPropsV2Legacy<TColumnName>,
>({
  columns,
  getSortProps,
  isSticky,
}: TableHeaderLegacyProps<TColumnName, TColumnProps>) => {
  const [t] = useTranslation(['backend', 'common']);
  const isMobile = useMediaQuery({ query: Media.xsMax });

  return (
    <TableLegacy.HeadRow>
      {columns.map((column) => {
        const { name, title, toggleSelectAll, columnSwitch, hidden } = column;
        const sortField = (column.customSortKey as TColumnName) ?? column.name;

        if (hidden || (isMobile && column.contentType === TableCellContentTypeLegacy.CHECKBOX && !toggleSelectAll)) {
          return null;
        }

        return (
          <TableLegacy.Head
            {...column}
            key={name}
            order={column.sortable && getSortProps ? getSortProps(sortField).order : undefined}
            handleChangeOrder={column.sortable && getSortProps ? getSortProps(sortField).handleChangeOrder : undefined}
            $sticky={isSticky}>
            {title}
            {columnSwitch}
            {toggleSelectAll && (
              <span data-testid={toggleSelectAll.dataTestId}>
                <Checkbox
                  label={t('common:SELECT_ALL')}
                  checked={toggleSelectAll.checked}
                  onChange={toggleSelectAll.onChange}
                  disabled={toggleSelectAll.disabled}
                  indeterminate={toggleSelectAll.indeterminate}
                />
              </span>
            )}
          </TableLegacy.Head>
        );
      })}
    </TableLegacy.HeadRow>
  );
};

export default TableHeaderLegacy;
